/**
 * PurchaseOrderList Component
 *
 * This component displays the Purchase Order (PO) list for a specific project.
 * It is structured to handle supplier POs, including active/inactive tabs,
 * filtering, pagination, and actions like viewing, editing, receiving stock,
 * and invoicing.
 *
 * Refactor Summary:
 * - Converted column definitions (subContractorColums/suppilerColumns) to useMemo hooks.
 * - Converted all action handlers and data fetching functions (_getPOList, _getTabCounts, etc.) to useCallback.
 * - Consolidated useEffect dependencies to control data fetching efficiently.
 * - Memoized Popover menu rendering logic (renderMenu) for performance.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import '../scss/task.scss'
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import Popover from 'react-bootstrap/Popover';
import { Route, Routes, useNavigate, useParams, useLocation } from "react-router";
import ViewPo from "./view-po";
import CreatePurchaseOrder from "./create-purchase-order";
import { PROJECT_MANAGEMENT, VENDOR_PAYMENT } from "../../../constants/route-paths";
import idx from "idx";
import { resolveProjectType } from "../utils/resolve-project-type";
import { numberFormat } from "../../../util/currency-formatter";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { deletePurchaseOrder, downloadProjectPO, getPOList, getPurchaseOrderTabCounts } from "../../../actions/project-management.action";
import { getProjectCustomer } from "../../../actions/invoice.action";
import { addProjectAction } from "../../../slice/project-summary";
import { useSelector } from "react-redux";
import { getGlobalSettingTemplateTypes } from "../../../actions/global-setting-template.action";
import DownloadDialog from "../../../components/download-dialog";
import DeleteDialog from "../../../components/delete-dialog";
import CustomTable from "../../../components/table/table";
import PayInvoice from "../../vendor-payment/pay-invoice";
import { PerPageCount, TemplateType } from "../../../util/enums";
import moment from "moment";


/**
 * Component for listing and managing Purchase Orders within project execution.
 *
 * @param {Object} props - Component properties.
 * @param {boolean} [props.wSupplierPermission] - Supplier write permission flag.
 * @returns {React.ReactElement} Purchase Order list view component.
 */
export default function PurchaseOrderList({ wSupplierPermission, ...props }) {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const initialSubTab = searchParams.get("subTab") || searchParams.get("returnSubTab") || "active";

  const [completed, setCompleted] = useState([]);
  const [pending, setPending] = useState([]);
  const [activeTab, setActiveTab] = useState(initialSubTab);
  const [tabCounts, setTabCounts] = useState({ active: 0, inactive: 0 });
  const [filter] = useState("pending"); // Kept for column switching logic
  const [poId, setPoId] = useState();
  const [poNo, setPoNo] = useState();
  const [poData, setPoData] = useState({});
  const [showPayInvoice, setShowInvoice] = useState(false);
  const [vendorId, setVedorId] = useState(null);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [downloadData, setDownloadData] = useState({ open: false, id: "" });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [limit, setLimit] = useState(PerPageCount.DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const projectSummary = useSelector(state => state.projectSummary) || {};
  const projectDetail = useSelector(state => state.projectDetail) || {};
  const projectState = useSelector(state => state.project);
  const entitlements = useSelector(state => state.user.entitlements);
  const [projectName, setProjectName] = useState(
    idx(projectSummary, _ => _.project.projectName) ||
    idx(projectDetail, _ => _.project.projectName) ||
    ""
  );
  // Replaced safeIdx
  const projectType = resolveProjectType(
    searchParams.get("project-type") ||
    idx(projectSummary, _ => _.project.projectDataType) ||
    idx(projectDetail, _ => _.project.projectDataType),
    projectState
  );

  const isDetailOrFormView = location.pathname.includes('/view/') ||
    location.pathname.includes('/create-purchase-order') ||
    location.pathname.includes('/edit/');

  // --- API Callbacks ---

  const _getPOList = useCallback(() => {
    setIsLoading(true);
    dispatch(getPOList({ projectId: projectId, vendorType: "Supplier", tab: activeTab, page: currentPage, limit, search }))
      .then(poList => {
        const { list } = poList;
        const formattedList = Array.isArray(list) ? list.map((res) => {
          const vendorName = idx(res, _ => _.vendor.business_name);
          const type = res.vendorType;
          return { id: res.id, vendorName, type, ...res };
        }) : [];

        if (activeTab === 'active') {
          setPending(formattedList);
        } else {
          setCompleted(formattedList);
        }
      })
      .catch((e) => console.error('Failed to fetch PO list:', e))
      .finally(() => setIsLoading(false));
  }, [dispatch, projectId, activeTab, currentPage, limit, search]);

  const _getTabCounts = useCallback(() => {
    dispatch(getPurchaseOrderTabCounts({ projectId, vendorType: "Supplier", search }))
      .then((counts) => {
        setTabCounts({
          active: counts?.active ?? 0,
          inactive: counts?.inactive ?? 0
        });
      })
      .catch((error) => {
        console.error('Error calculating tab counts:', error);
        setTabCounts({ active: 0, inactive: 0 });
      });
  }, [dispatch, projectId, search]);

  const gettemplate = useCallback(async (type = TemplateType.PURCHASE_ORDER) => {
    try {
      let _templateOptions = [];
      const templateData = await dispatch(getGlobalSettingTemplateTypes(type, null));
      if (Array.isArray(templateData)) {
        templateData.forEach((item) => {
          _templateOptions.push({
            value: item.id,
            key: item.name,
          });
        });
      }
      setTemplateOptions(_templateOptions);
    } catch (e) {
      console.error('Failed to fetch templates:', e);
    }
  }, [dispatch]);

  const deleteDialogState = useCallback(() => {
    setOpenDeleteDialog(prev => !prev);
  }, []);

  const deleteHandler = useCallback(async () => {
    deleteDialogState();
    if (poId) {
      await dispatch(deletePurchaseOrder(poId, "Purchase Order Deleted successfully"));
      _getPOList();
      _getTabCounts();
    }
  }, [dispatch, poId, deleteDialogState, _getPOList, _getTabCounts]);

  const handleDownloadDialog = useCallback((id = "", templateType = TemplateType.PURCHASE_ORDER, overrideData = null) => {
    const currentId = id && (typeof id === 'string' || typeof id === 'number') ? id : poId;
    // When opened from po-list menu directly, it's an event object, so check if it's a string/number
    const actualId = (typeof id === 'object') ? poId : currentId;
    const actualType = (typeof id === 'object') ? TemplateType.PURCHASE_ORDER : templateType;

    setDownloadData(prev => {
      const isOpening = !prev.open;
      const newDownloadData = { ...prev, open: isOpening, id: actualId || "", overrideData };
      if (isOpening) {
        gettemplate(actualType);
      }
      return newDownloadData;
    });
  }, [poId, gettemplate]);

  const downloadFile = useCallback(async (fileFormat = "pdf", tempId) => {
    try {
      if (fileFormat === "pdf" || fileFormat === "ALL") {
        await dispatch(downloadProjectPO(downloadData.id, fileFormat, tempId, undefined, downloadData.overrideData)).catch(e => console.error(e));
      }
      if (fileFormat === "docx" || fileFormat === "ALL") {
        await dispatch(downloadProjectPO(downloadData.id, fileFormat, tempId, undefined, downloadData.overrideData)).catch(e => console.error(e));
      }
      handleDownloadDialog();
    } catch (e) {
      console.error('Download failed:', e);
    }
  }, [dispatch, downloadData.id, downloadData.overrideData, handleDownloadDialog]);

  // --- Effects ---

  // Load project metadata once on mount if missing
  useEffect(() => {
    if (!projectId) return;
    const isCurrentProjectInRedux =
      projectSummary?.project?.projectId === projectId &&
      Boolean(projectSummary?.project?.projectName);

    if (isCurrentProjectInRedux) {
      setProjectName(projectSummary.project.projectName);
      return;
    }
    dispatch(getProjectCustomer(projectId)).then((res) => {
      if (res && res.name) {
        setProjectName(res.name);
        dispatch(addProjectAction({
          projectId: res.id,
          projectName: res.name,
          projectDataType: res.projectType,
          projectType: res.type
        }));
      }
    }).catch(e => console.error('Failed to fetch project customer:', e));
  }, [projectId, dispatch]);

  useEffect(() => {
    const currentName =
      idx(projectSummary, _ => _.project.projectName) ||
      idx(projectDetail, _ => _.project.projectName);
    if (currentName && currentName !== projectName) {
      setProjectName(currentName);
    }
  }, [projectSummary, projectDetail, projectName]);

  // Tab counts reload only on project or search change
  useEffect(() => {
    if (isDetailOrFormView || !projectId) return;
    _getTabCounts();
  }, [isDetailOrFormView, projectId, _getTabCounts]);

  // PO List data reload on project, tab, pagination, or search change
  useEffect(() => {
    if (isDetailOrFormView || !projectId) return;
    _getPOList();
  }, [isDetailOrFormView, projectId, _getPOList]);

  // --- Column Definition (useMemo for stability) ---

  const subContractorColumns = useMemo(() => ([
    {
      title: "PO No",
      field: "id",
      dataCy: "po-no",
      render: (rowData) => (
        <Link to={`view/${rowData.u_id}`}>{rowData.f_id || rowData.id}</Link>
      ),
    },
    {
      title: "Total",
      field: "totalCost",
      dataCy: "total",
      render: (rowData) => `${numberFormat(rowData.totalCost)}`,
    },
    { title: "Vendor", field: "vendorName", dataCy: "vendor-name", render: (rowData) => rowData.vendorName },
    {
      title: "Vendor Type",
      field: "type",
      dataCy: "vendor-type",
      render: (rowData) =>
        rowData.type === "sub-contractor" ? "Contractor" : rowData.type,
    },
  ]), []);

  const supplierColumns = useMemo(() => [ // Corrected to useMemo and fixed typo
    {
      title: "PO No",
      field: "id",
      dataCy: "po-no",
      render: (rowData) => (
        <Link to={`view/${rowData.u_id}`}>{rowData.f_id || rowData.id}</Link>
      ),
    },
    {
      title: 'Date Ordered',
      field: 'createdAt',
      dataCy: 'date-ordered',
      render: (rowData) => moment(rowData.createdAt).format('MMM DD, YYYY')
    },
    {
      title: 'Delivery Date',
      field: 'deliveryDate',
      dataCy: 'delivery-date',
      render: (rowData) => {
        const today = moment();
        const deliveryDate = moment(rowData.deliveryDate);
        const isAfterDeliveryDate = today.isAfter(deliveryDate, 'day');
        const isNotFullyReceived = rowData.materialstatus !== "Fully Received" && rowData.materialstatus !== "Over Received";
        const isOverdue = isAfterDeliveryDate && isNotFullyReceived;

        return isOverdue ? <span className="text-danger" data-cy="overdue-delivery-date">{rowData.deliveryDate ? moment(rowData.deliveryDate).format('MMM DD, YYYY') : 'Not Specified'}</span> : <span data-cy="normal-delivery-date">{rowData.deliveryDate ? moment(rowData.deliveryDate).format('MMM DD, YYYY') : 'Not Specified'}</span>
      }
    },
    { title: "Vendor", field: "vendorName", dataCy: "vendor-name", render: (rowData) => rowData.vendorName },
    { title: 'Ordered By', field: 'userName', dataCy: "user-name", render: (rowData) => rowData.userName ?? 'N/A' },
    { title: "Material Status", field: "materialstatus", dataCy: "material-status", render: (rowData) => rowData.materialstatus },
    { title: "Invoice Status", field: "invoicestatus", dataCy: "invoice-status", render: (rowData) => rowData.invoicestatus },
    {
      title: "Total",
      field: "totalCost",
      dataCy: "total",
      render: (rowData) => `${numberFormat(rowData.totalCost)}`,
    }
  ], []);

  const currentColumns = filter === "Sub-Contractors" ? subContractorColumns : supplierColumns;
  // --- UI/Modal Handlers (useCallback for stability) ---

  const handlePayInvoiceModal = useCallback(() => {
    setShowInvoice(prev => !prev);
    // If closing the modal (status becomes false), reload the data/counts
    if (showPayInvoice) {
      _getPOList();
      _getTabCounts();
    }
  }, [showPayInvoice, _getPOList, _getTabCounts]);

  const handleMenu = useCallback((rowData) => {
    setPoId(rowData.u_id);
    setPoNo(rowData.id);
    setPoData(rowData);
    // Replaced safeIdx
    setVedorId(idx(rowData, _ => _.vendor.id));
  }, []);

  // --- Navigation Handlers (useCallback for stability) ---

  const viewProject = useCallback(() => navigate(`/projects/view/${projectId}`), [navigate, projectId]);
  // Replaced safeIdx
  const viewProjectList = useCallback(() => navigate(`/projects?project-type=${projectType}`), [navigate, projectType]);
  const viewPO = useCallback(() => navigate(`view/${poId}`), [navigate, poId]);
  const editPO = useCallback(() => navigate(`edit/${poId}`), [navigate, poId]);
  const createPO = useCallback(() => navigate(`create-purchase-order`), [navigate]);
  const receiveStock = useCallback(() => navigate(`/${PROJECT_MANAGEMENT}/receive-stock/${projectId}/${vendorId}/${poId}`), [navigate, projectId, vendorId, poId]);
  const returnStock = useCallback(() => navigate(`/${PROJECT_MANAGEMENT}/return-stock/${projectId}/${vendorId}/${poId}`), [navigate, projectId, vendorId, poId]);
  const viewInvoices = useCallback(() => navigate(`/${VENDOR_PAYMENT}?project=${projectId}&poId=${poId}`), [navigate, projectId, poId]);


  // --- Pagination and Search Handlers (useCallback for stability) ---

  const pageHandler = useCallback((page) => {
    const newPage = page > 0 ? page : 1;
    setCurrentPage(newPage);
  }, []);

  const limitChangeHandler = useCallback((newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  }, []);

  const searchHandler = useCallback((value) => {
    if (value !== search) {
      setSearch(value);
      setCurrentPage(1);
    }
  }, [search]);

  // --- Popover Menu Rendering (useCallback for stability) ---

  const renderMenu = useCallback((item) => {
    const isPending = activeTab === 'active';

    const renderPendingMenu = () => (
      <>
        <li onClick={viewPO} data-cy="view-po">View PO</li>
        {wSupplierPermission && item.materialstatus === "None Received" && <li onClick={editPO} data-cy="edit-po">Edit PO</li>}
        <li onClick={handleDownloadDialog} data-cy="download-po">Download PO</li>
        {wSupplierPermission && item.materialstatus !== "Fully Received" && item.materialstatus !== "Over Received" && <li
          className={!(entitlements.receiveStock) && 'disabled'}
          onClick={() => (entitlements.receiveStock) && receiveStock()}
          data-cy="receive-stock"
        >
          Receive Stock
        </li>}
        {wSupplierPermission && item.materialstatus !== "None Received" && (
          <li onClick={returnStock} data-cy="return-stock">
            Return Stock
          </li>
        )}
        {wSupplierPermission && item.invoicestatus !== "Fully Invoiced" && <li onClick={handlePayInvoiceModal} data-cy="receive-invoice">Receive Invoice</li>}
        <li onClick={viewInvoices}>View Invoices</li>
        {wSupplierPermission && <li onClick={deleteDialogState} data-cy="delete-po">Delete</li>}
      </>
    );

    const renderCompletedMenu = () => (
      <>
        <li onClick={viewPO} data-cy="view-po"> View PO</li>
        <li onClick={handleDownloadDialog} data-cy="download-po">Download PO</li>
        {wSupplierPermission && <li onClick={returnStock} data-cy="return-stock">Return Stock</li>}
        <li onClick={viewInvoices} data-cy="view-invoices">View Invoices</li>
      </>
    );

    return (
      <Popover id="popover-basic" data-cy="po-list-table-menu">
        <Popover.Body>
          <ul className="action-block-list">
            {isPending ? renderPendingMenu() : renderCompletedMenu()}
          </ul>
        </Popover.Body>
      </Popover>
    );
  }, [activeTab, viewPO, editPO, handleDownloadDialog, wSupplierPermission, entitlements.receiveStock, receiveStock, handlePayInvoiceModal, viewInvoices, deleteDialogState, returnStock]);


  return (
    <>
      <DownloadDialog
        {...downloadData}
        downloadFile={downloadFile}
        handleDownloadDialog={handleDownloadDialog}
        templateOptions={templateOptions}
        downloadModuleName="Purchase Order"
        dataCy="download-po-dialog"
      />
      <DeleteDialog
        open={openDeleteDialog}
        id={poId}
        deleteHandler={deleteHandler}
        dialogClose={deleteDialogState}
        moduleName="Purchase Order"
        description={`Purchase Order - ${poNo}`}
        dataCy="delete-po-dialog"
      />
      <Routes>
        <Route
          path={`view/:poId`}
          element={<ViewPo poData={poData} />}
        />
        <Route
          path={`create-purchase-order`}
          element={<CreatePurchaseOrder />}
        />
        <Route
          path={`edit/:poId`}
          element={<CreatePurchaseOrder />}
        />
        <Route
          path=""
          exact
          element={
            <>
              {showPayInvoice && (
                <PayInvoice
                  projectId={projectId}
                  vendorId={vendorId}
                  poId={poId}
                  poNo={poNo}
                  isSubContractor={false}
                  workType="project"
                  handleClose={handlePayInvoiceModal}
                  payInvoiceType={"purchaseOrder"}
                  handleDownloadDialog={handleDownloadDialog}
                  getPOList={_getPOList}
                  dataCy="receive-invoice-po-modal"
                />
              )}
              <div className="purchase-order-block" data-cy="po-list-container">
                <div className="row">
                  <div className="w-auto">
                    <h2 className="content-heading text-uppercase">Purchase Order List</h2>
                    <div className="col-12 breadcrumb-block p-0">
                      <Breadcrumb>
                        <Breadcrumb.Item onClick={viewProjectList} data-cy="projects-breadcrumb">PROJECTS</Breadcrumb.Item>
                        <Breadcrumb.Item onClick={viewProject} data-cy="project-breadcrumb">{projectName || idx(projectSummary, _ => _.project.projectName) || idx(projectDetail, _ => _.project.projectName)}</Breadcrumb.Item>
                        <Breadcrumb.Item active>Purchase Order List</Breadcrumb.Item>
                      </Breadcrumb>
                    </div>
                  </div>
                  <div className="w-auto ms-auto mt-auto">
                    {wSupplierPermission && (<button
                      type="submit"
                      className="btn-ch ms-auto btn btn-primary secondarybtn btn-bg-brand-4"
                      onClick={createPO}
                      data-cy="create-po"
                    >
                      Create Purchase Order
                    </button>)}
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <Tabs
                      activeKey={activeTab}
                      id="po-list-tabs"
                      className="mb-3"
                      onSelect={(selectedTab) => setActiveTab(selectedTab || "active")}
                      tabClassName="po-list-tabs"
                    >
                      <Tab eventKey="active" title={`Active (${tabCounts.active})`} tabClassName="active-tab">
                        <CustomTable
                          columns={currentColumns}
                          data={pending}
                          isAction={true}
                          handleMenu={handleMenu}
                          dataCy="po-list-active-table"
                          paginationCount={tabCounts.active}
                          currentPage={currentPage}
                          pageHandler={pageHandler}
                          limitChangeHandler={limitChangeHandler}
                          itemsPerPage={limit}
                          searchHandler={searchHandler}
                          isLoading={isLoading}
                          externalSearchValue={search}
                          externalSearchHandler={searchHandler}
                          menu={renderMenu}
                          fileName='Pending PO List'
                        />
                      </Tab>
                      <Tab eventKey="inactive" title={`Inactive (${tabCounts.inactive})`} tabClassName="completed-tab">
                        <CustomTable
                          columns={currentColumns}
                          data={completed}
                          isAction={true}
                          handleMenu={handleMenu}
                          dataCy="po-list-completed-table"
                          paginationCount={tabCounts.inactive}
                          currentPage={currentPage}
                          pageHandler={pageHandler}
                          limitChangeHandler={limitChangeHandler}
                          itemsPerPage={limit}
                          searchHandler={searchHandler}
                          isLoading={isLoading}
                          externalSearchValue={search}
                          externalSearchHandler={searchHandler}
                          menu={renderMenu}
                          fileName='Completed PO List'
                        />
                      </Tab>
                    </Tabs>
                  </div>
                </div>
              </div>
            </>
          }
        />
      </Routes>
    </>
  )
}